import React, { useEffect, useState } from 'react';
import { createEntity } from '../data/entities';
import { useLiveConfig } from '../data/firestore';
import { prefersReducedMotion } from './charts/chartTheme';

function useTick(ms, length) {
  const [i, setI] = useState(() => (prefersReducedMotion() ? length - 1 : 0));
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % length), ms);
    return () => clearInterval(t);
  }, [ms, length]);
  return i;
}

function Preview({ title, children, caption }) {
  return (
    <article className="aio-preview">
      <h3>{title}</h3>
      <div className="aio-stage" aria-hidden="true">{children}</div>
      <p className="cs-help">{caption}</p>
    </article>
  );
}

function SetupStage() {
  const step = useTick(1400, 3);
  return (
    <Preview title="Setup" caption="Name, greeting, and the prompt.">
      <div className={`aio-stage-line ${step >= 0 ? 'on' : ''}`}>Intake</div>
      <div className={`aio-stage-line dim ${step >= 1 ? 'on' : ''}`}>How can I help?</div>
      <div className={`aio-stage-line dim ${step >= 2 ? 'on' : ''}`}>Answer from the records you allow.</div>
    </Preview>
  );
}

function ConfigStage() {
  const step = useTick(900, 5);
  const reads = ['project', 'pipeline'];
  return (
    <Preview title="Config" caption="Reads, proposals, and the launcher.">
      <div className="aio-stage-row">
        {reads.map((name, i) => (
          <span key={name} className={step > i ? 'on' : ''}>read {name}</span>
        ))}
        <span className={step > 2 ? 'on' : ''}>propose pipeline</span>
      </div>
      <div className={`aio-stage-launch ${step > 3 ? 'right' : ''}`}>Chat</div>
    </Preview>
  );
}

function WorkflowStage() {
  const step = useTick(1500, 3);
  const labels = ['Propose', 'Gate', 'Commit'];
  return (
    <Preview title="Workflow" caption="The model proposes. A gate holds. The runtime commits.">
      <div className="aio-stage-rail">
        <i style={{ width: `${(step / 2) * 100}%` }} />
        {labels.map((label, i) => (
          <span key={label} className={step === i ? 'on' : ''}>{label}</span>
        ))}
      </div>
    </Preview>
  );
}

function RolesStage() {
  const step = useTick(1200, 4);
  const roles = ['Research', 'Security', 'Spec'];
  return (
    <Preview title="Several roles" caption="A coordinator names library roles for one objective.">
      <div className={`aio-stage-line ${step >= 0 ? 'on' : ''}`}>Coordinator</div>
      <div className="aio-stage-row">
        {roles.map((name, i) => (
          <span key={name} className={step > i ? 'on' : ''}>{name}</span>
        ))}
      </div>
    </Preview>
  );
}

function randomToken(bytes = 12) {
  const arr = new Uint8Array(bytes);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export default function AgentSetup({ org, user, toolable, entitled }) {
  const { doc: config } = useLiveConfig();
  const chatModel = config?.agents?.model || 'gpt-4o-mini';
  const voiceModel = config?.voice?.model || config?.voice?.provider || 'shared voice';
  const [name, setName] = useState('');
  const [greeting, setGreeting] = useState('Hi! How can I help?');
  const [prompt, setPrompt] = useState('');
  const [reads, setReads] = useState([]);
  const [writes, setWrites] = useState([]);
  const [accent, setAccent] = useState('#4f46e5');
  const [side, setSide] = useState('right');
  const [launcher, setLauncher] = useState('Chat');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggle = (list, setList, entity) => {
    setList(list.includes(entity) ? list.filter((e) => e !== entity) : [...list, entity]);
  };

  return (
    <section className="aio-setup" aria-label="Create an agent">
      <div className="aio-previews">
        <SetupStage />
        <ConfigStage />
        <WorkflowStage />
        <RolesStage />
      </div>
      <p className="cs-help aio-shared">
        Chat model {chatModel}. Voice {voiceModel}. Both are shared. A workflow commit and a coordinator live on nQube.
      </p>
      {entitled && (
        <form
          className="aio-create"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!name.trim() || busy) return;
            setBusy(true);
            setError('');
            try {
              await createEntity('agent', { org: org.id }, {
                name: name.trim(),
                publicToken: randomToken(),
                createdBy: user.uid,
                greeting: greeting.trim() || 'Hi! How can I help?',
                systemPrompt: prompt,
                allowReads: reads,
                allowWrites: writes,
                theme: {
                  accent: /^#[0-9a-fA-F]{6}$/.test(accent) ? accent : '#4f46e5',
                  side: side === 'left' ? 'left' : 'right',
                  launcher: String(launcher || 'Chat').slice(0, 12),
                },
              });
              setName('');
              setPrompt('');
              setReads([]);
              setWrites([]);
            } catch (err) {
              setError(err.message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <input className="cs-input" placeholder="Name" aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="cs-input" placeholder="Greeting" aria-label="Greeting" value={greeting} onChange={(e) => setGreeting(e.target.value)} />
          <textarea className="cs-input aio-prompt" placeholder="System prompt" aria-label="System prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
          <div className="aio-create-access">
            <span className="cs-help">Read</span>
            {toolable.map((entity) => (
              <button key={`r-${entity}`} type="button" className={`cs-btn cs-btn-sm ${reads.includes(entity) ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => toggle(reads, setReads, entity)}>{entity}</button>
            ))}
            <span className="cs-help">Propose</span>
            {toolable.map((entity) => (
              <button key={`w-${entity}`} type="button" className={`cs-btn cs-btn-sm ${writes.includes(entity) ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => toggle(writes, setWrites, entity)}>{entity}</button>
            ))}
          </div>
          <div className="aio-create-look">
            <input aria-label="Accent" type="color" value={accent} onChange={(e) => setAccent(e.target.value)} />
            <select className="cs-input" aria-label="Launcher side" value={side} onChange={(e) => setSide(e.target.value)}>
              <option value="right">Right</option>
              <option value="left">Left</option>
            </select>
            <input className="cs-input" aria-label="Launcher label" maxLength={12} value={launcher} onChange={(e) => setLauncher(e.target.value)} />
            <button className="cs-btn cs-btn-primary" disabled={!name.trim() || busy} type="submit">Create</button>
          </div>
          {error && <p className="cs-help">{error}</p>}
        </form>
      )}
    </section>
  );
}
